import { hotkeysList } from '@/lists/hotkeys';

import type { Workflow } from '@heidi/agents/contracts';
import { normalizeCombo } from '@heidi/agents/data';

/**
 * Conflict detection for workflow shortcuts. Kept separate from the pure combo
 * helpers because it imports `@/lists/hotkeys` (the built-in hotkey catalog),
 * so lightweight consumers (badge, listener) don't pull that module in.
 */

// `hotkeys` entries are either a combo list or a direction-keyed map of combo
// lists; collect string leaves without leaning on `any`.
const collectCombos = (value: unknown, out: string[]): void => {
  if (typeof value === 'string') {
    out.push(value);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value as unknown[]) collectCombos(item, out);
    return;
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value as Record<string, unknown>)) {
      collectCombos(item, out);
    }
  }
};

const flattenBuiltInCombos = (): string[] => {
  const combos: string[] = [];
  for (const entry of Object.values(hotkeysList)) {
    collectCombos(entry.hotkeys, combos);
  }
  return combos;
};

/** Normalized combos already claimed by the app's built-in hotkeys. */
export const BUILT_IN_SHORTCUT_COMBOS: ReadonlySet<string> = new Set(
  flattenBuiltInCombos().map(normalizeCombo)
);

export type ShortcutConflict =
  | { kind: 'workflow'; workflowName: string }
  | { kind: 'builtin' };

type ConflictWorkflow = Pick<Workflow, 'workflow_id' | 'name' | 'shortcut'>;

/**
 * A combo can't be assigned if another of the user's workflows already owns it
 * (hard conflict). Collisions with a built-in hotkey are reported separately so
 * the UI can warn without blocking.
 */
export const detectShortcutConflict = (
  combo: string,
  workflows: ConflictWorkflow[],
  excludeWorkflowId: string
): ShortcutConflict | null => {
  const normalized = normalizeCombo(combo);
  const clash = workflows.find(
    (workflow) =>
      workflow.workflow_id !== excludeWorkflowId &&
      workflow.shortcut != null &&
      normalizeCombo(workflow.shortcut) === normalized
  );
  if (clash) return { kind: 'workflow', workflowName: clash.name };
  if (BUILT_IN_SHORTCUT_COMBOS.has(normalized)) return { kind: 'builtin' };
  return null;
};
